/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { FlightLegInfo } from '../../../../shared/entities/flightLeg';
import MissionPlanDetails from '../../../../shared/entities/missionPlanDetails';
import { selectFlightLegServiceAlerts } from '../../../../store';
import { setSelectedOverlaidFlightLegId } from '../../../../store/flightLegs/actions';
import { getServiceAlerts } from '../../../../store/flightLegServiceAlerts/actions';
import { formatPOExportMissoinCreatedDate } from '../../../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../utils/StringUtils';
import appTheme from '../../../common/theme/appTheme.json';
import { ContourDisplayEnum } from '../PerformanceOutlookExportModal';
import PerformanceOutlookFlightEventsList from '../PerformanceOutlookFlightEventsList';
import PerformanceOutlookLegInfo from '../PerformanceOutlookLegInfo';
import PerformanceOutlookHorizontalTimeline from '../TimelineChart/PerformanceOutlookHorizontalTimeline';
import { useHeatMapsEnabled } from './performanceOutlookExportHelperHooks';
import PerformanceOutlookExportLegend from './PerformanceOutlookExportLegend';
import PerformanceOutlookExportMapContainer from './PerformanceOutlookExportMapContainer';
import PerformanceOutlookExportPrintModeHeader from './PerformanceOutlookExportPrintModeHeader';

export const PO_EXPORT_COMPACT_PRINT_MAP_HEIGHT = 580;
export const PO_EXPORT_COMPACT_PRINT_MAP_WIDTH = '641px';
export const PO_EXPORT_COMPACT_PRINT_DEFAULT_WIDTH = 1100;
export const PO_EXPORT_COMPACT_HORIZ_TIMELINE_WIDTH = 1060;
export const PO_EXPORT_COMPACT_HORIZ_TIMELINE_HEIGHT = 175;
export const PO_EXPORT_COMPACT_FLIGHT_LEG_INFO_HEIGHT = 40;
export const PO_EXPORT_COMPACT_LEGEND_HEIGHT = 21;

const NUM_EVENTS_PER_LIST_IN_EXPORT = 16;

export const useStyles = makeStyles({
  flightLegInfoContainer: {
    width: PO_EXPORT_COMPACT_PRINT_MAP_WIDTH,
    height: PO_EXPORT_COMPACT_FLIGHT_LEG_INFO_HEIGHT,
    position: 'relative',
    display: 'block',
  },
  horizontalineTimelineContainer: {
    width: PO_EXPORT_COMPACT_PRINT_DEFAULT_WIDTH,
    height: PO_EXPORT_COMPACT_HORIZ_TIMELINE_HEIGHT,
    position: 'relative',
    '@media print': {
      marginTop: '-10px',
      marginLeft: '6px',
      pageBreakInside: 'avoid',
    },
  },
  missionContainer: {
    width: PO_EXPORT_COMPACT_HORIZ_TIMELINE_WIDTH,
    height: PO_EXPORT_COMPACT_LEGEND_HEIGHT,
    display: 'block',
    marginTop: '-50px',
  },
});

export interface PerfomanceOutlookCompactPrintProps {
  flightLeg: FlightLegInfo;
  missionPlan: MissionPlanDetails;
  userId: string;
  totalFlightLegs: number;
  contourDisplayType: ContourDisplayEnum;
}

const PerfomanceOutlookCompactPrint = (props: PerfomanceOutlookCompactPrintProps) => {
  const { flightLeg, missionPlan, userId, totalFlightLegs, contourDisplayType } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const serviceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const [mapId] = useState(`exportMap-${flightLeg.id}`);

  useEffect(() => {
    if (missionPlan.id && userId && flightLeg.id) {
      dispatch(getServiceAlerts(missionPlan.id, flightLeg.id, userId));
      dispatch(setSelectedOverlaidFlightLegId(mapId)(flightLeg.id));
    }
  }, [dispatch, missionPlan.id, userId, flightLeg.id, mapId]);

  useHeatMapsEnabled(contourDisplayType, mapId);

  if (!serviceAlerts) {
    return null;
  }
  return (
    <Box
      sx={{
        display: 'block',
        position: 'relative',
        '@media print': {
          position: 'relative',
          display: 'block',
          margin: 0,
          pageBreakAfter: 'always',
        },
      }}
    >
      <Box
        sx={{
          width: `${PO_EXPORT_COMPACT_PRINT_DEFAULT_WIDTH}px`,
          position: 'relative',
          display: 'block',
          margin: 'auto',
          '@media print': {
            margin: 0,
          },
        }}
      >
        {/* flight leg header */}
        <PerformanceOutlookExportPrintModeHeader flightLeg={flightLeg} totalFlightLegs={totalFlightLegs} />
        <Box sx={{ display: 'block', width: `${PO_EXPORT_COMPACT_PRINT_DEFAULT_WIDTH}px` }}>
          {/* left container */}
          <Box
            sx={{
              display: 'inline-block',
              verticalAlign: 'top',
              width: PO_EXPORT_COMPACT_PRINT_MAP_WIDTH,
            }}
          >
            {/* map */}
            <Box
              sx={{
                width: PO_EXPORT_COMPACT_PRINT_MAP_WIDTH,
                height: `${PO_EXPORT_COMPACT_PRINT_MAP_HEIGHT}px`,
                display: 'block',
                position: 'relative',
              }}
            >
              <PerformanceOutlookExportMapContainer
                flightLeg={flightLeg}
                missionPlan={missionPlan}
                serviceAlerts={serviceAlerts[flightLeg.id]}
                userId={userId}
                mapId={mapId}
                topPosition="0px"
              />
            </Box>
            {/* flight info */}
            <Box className={classes.flightLegInfoContainer}>
              <PerformanceOutlookLegInfo
                missionPlan={missionPlan}
                serviceAlerts={serviceAlerts}
                flightLegId={flightLeg.id}
                contourDisplayType={contourDisplayType}
                serviceTiersSlaChartEnabled={false}
              />
            </Box>
          </Box>
          {/* right container */}
          <Box
            sx={{
              width: '439px',
              marginLeft: '20px',
              display: 'inline-block',
              verticalAlign: 'top',
            }}
          >
            <PerformanceOutlookFlightEventsList
              key={flightLeg.id}
              isPOExportPage={true}
              flightLegId={flightLeg.id}
              breakDataNum={NUM_EVENTS_PER_LIST_IN_EXPORT}
            />
          </Box>
        </Box>
        <Box className={classes.horizontalineTimelineContainer}>
          <PerformanceOutlookHorizontalTimeline
            key={flightLeg.id}
            chartWidth={PO_EXPORT_COMPACT_HORIZ_TIMELINE_WIDTH}
            chartTopPosition={0}
            flightLegId={flightLeg.id}
            mapId={mapId}
            disableTimelineHover
          />
        </Box>
        <Box className={classes.missionContainer}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                fontFamily: appTheme.viasatFontFamilyRegular,
                color: appTheme.primaryTextColor,
                fontSize: '12px',
                lineHeight: '21px',
                fontWeight: 500,
                width: '30%',
                '@media print': {
                  color: 'black',
                },
              }}
            >
              Mission created:
              {missionPlan.creationDate
                ? ` ${formatPOExportMissoinCreatedDate(missionPlan.creationDate)}Z`
                : EMPTY_FIELD_STRING_PLACEHOLDER}
            </Box>

            <Box sx={{ position: 'relative', width: '70%' }}>
              <PerformanceOutlookExportLegend flightLegId={flightLeg.id} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PerfomanceOutlookCompactPrint;

export const StyledFlightLegHead = styled.h1`
  display: inline-block;
  width: 50%;
  font-family: ${appTheme.viasatFontFamilyRegular};
  color: ${appTheme.primaryTextColor};
  font-size: 25px;
  font-weight: 500;
  @media print {
    color: black;
    margin: 0;
    font-size: 16px;
  }
`;
